import Dexie, { Table } from 'dexie';

// DAG-005 hardening: the IndexedDB record used to store full
// generated profiles, full teamMemberDetails (incl. mail/department/
// jobTitle), the scraped client website body, and the manual project
// context. That defeated the localStorage/sessionStorage minimization
// added in Round 1 — governed proposal content was still pinned to
// the endpoint indefinitely. The persistence layer is now an opaque
// pointer only: proposalId + light display metadata. Profiles and
// context are re-fetched from the backend (which has object-level
// authorization since Round 1 T1) when the user resumes work.
export interface SavedProposal {
  proposalId: string;
  clientName: string;
  clientUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  formatType: 'full' | 'one-slider' | 'intro' | 'web-bio';
  /** Number of profiles in the proposal — display-only, no PII. */
  profileCount: number;
}

export interface ProposalHistoryItem {
  proposalId: string;
  clientName: string;
  createdAt: Date;
  profileCount: number;
}

class ProposalDatabase extends Dexie {
  proposals!: Table<SavedProposal>;

  constructor() {
    super('ProposalDB');

    this.version(1).stores({
      proposals: 'proposalId, clientName, createdAt, updatedAt'
    });

    // Version 2: Add createdBy field for user filtering
    this.version(2).stores({
      proposals: 'proposalId, clientName, createdAt, updatedAt, createdBy'
    }).upgrade(trans => {
      return trans.table('proposals').toCollection().modify(proposal => {
        proposal.createdBy = proposal.createdBy || 'unknown';
      });
    });

    // Version 3: Add teamMemberDetails field
    this.version(3).stores({
      proposals: 'proposalId, clientName, createdAt, updatedAt, createdBy'
    }).upgrade(trans => {
      return trans.table('proposals').toCollection().modify(proposal => {
        if (!proposal.teamMemberDetails && proposal.profiles) {
          proposal.teamMemberDetails = proposal.profiles.map((profile: any) => ({
            id: profile.userId || profile.user_id || profile.id,
            displayName: profile.display_name || profile.name || profile.userId || 'Unknown'
          }));
        }
      });
    });

    // Version 4 (DAG-005): pointer-only schema. Existing records get
    // their bulk fields stripped — profileCount is preserved from
    // the old `profiles` array length so the history list still shows
    // a useful count, but governed content (profile bodies, scraped
    // website, manual context, team member PII) is wiped from local
    // storage.
    this.version(4).stores({
      proposals: 'proposalId, clientName, createdAt, updatedAt, createdBy'
    }).upgrade(trans => {
      return trans.table('proposals').toCollection().modify((proposal: any) => {
        if (typeof proposal.profileCount !== 'number') {
          proposal.profileCount = Array.isArray(proposal.profiles)
            ? proposal.profiles.length
            : 0;
        }
        delete proposal.profiles;
        delete proposal.teamMembers;
        delete proposal.teamMemberDetails;
        delete proposal.scraped_content;
        delete proposal.manual_context;
      });
    });
  }
}

export class ProposalPersistenceService {
  private db: ProposalDatabase;

  constructor() {
    this.db = new ProposalDatabase();
  }

  async saveProposal(proposal: SavedProposal): Promise<void> {
    try {
      // Update the updatedAt timestamp
      proposal.updatedAt = new Date();
      
      // Use put to insert or update
      await this.db.proposals.put(proposal);
      
      console.log('Proposal saved to IndexedDB:', proposal.proposalId);
    } catch (error) {
      console.error('Failed to save proposal to IndexedDB:', error);
      throw error;
    }
  }

  async getProposal(proposalId: string): Promise<SavedProposal | undefined> {
    try {
      const proposal = await this.db.proposals.get(proposalId);
      console.log('Retrieved proposal from IndexedDB:', proposal);
      return proposal;
    } catch (error) {
      console.error('Failed to get proposal from IndexedDB:', error);
      throw error;
    }
  }

  async getLatestProposal(userId?: string): Promise<SavedProposal | undefined> {
    try {
      let query = this.db.proposals.orderBy('updatedAt');
      
      // If userId is provided, filter by it
      if (userId) {
        const allProposals = await query.reverse().toArray();
        const userProposals = allProposals.filter(p => p.createdBy === userId);
        return userProposals[0];
      }
      
      // Otherwise return the latest regardless of user (backward compatibility)
      const proposals = await query.reverse().limit(1).toArray();
      return proposals[0];
    } catch (error) {
      console.error('Failed to get latest proposal from IndexedDB:', error);
      throw error;
    }
  }

  async listUserProposals(userId?: string): Promise<ProposalHistoryItem[]> {
    try {
      let proposals = await this.db.proposals.toArray();

      if (userId) {
        proposals = proposals.filter(p => p.createdBy === userId);
      }

      const historyItems: ProposalHistoryItem[] = proposals.map(p => ({
        proposalId: p.proposalId,
        clientName: p.clientName,
        createdAt: p.createdAt,
        // profileCount is now a first-class field on the pointer
        // record (DAG-005); fall back to 0 for records that somehow
        // escaped the v4 upgrade.
        profileCount: p.profileCount ?? 0
      }));

      historyItems.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      console.log(`Listed ${historyItems.length} proposals from IndexedDB for user: ${userId || 'all'}`);
      return historyItems;
    } catch (error) {
      console.error('Failed to list proposals from IndexedDB:', error);
      throw error;
    }
  }

  async deleteProposal(proposalId: string): Promise<void> {
    try {
      await this.db.proposals.delete(proposalId);
      console.log('Proposal deleted from IndexedDB:', proposalId);
    } catch (error) {
      console.error('Failed to delete proposal from IndexedDB:', error);
      throw error;
    }
  }

  async deleteAllProposals(): Promise<void> {
    try {
      await this.db.proposals.clear();
      console.log('All proposals cleared from IndexedDB');
    } catch (error) {
      console.error('Failed to clear proposals from IndexedDB:', error);
      throw error;
    }
  }

  async getProposalsByClient(clientName: string): Promise<SavedProposal[]> {
    try {
      const proposals = await this.db.proposals
        .where('clientName')
        .equals(clientName)
        .toArray();
      
      // Sort by updated date (most recent first)
      proposals.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      
      return proposals;
    } catch (error) {
      console.error(`Failed to get proposals for client ${clientName}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const proposalPersistenceService = new ProposalPersistenceService();